// src/components/TrialBanner.jsx
// Shows after day 15 as a warning, hard message after day 30

import { useState } from 'react';
import { usePlan } from '../hooks/usePlan';
import { AlertTriangle, X, Lock, Mail } from 'lucide-react';

export default function TrialBanner() {
  const { plan, showTrialWarning, isTrialExpired, trialDaysRemaining } = usePlan();
  const [dismissed, setDismissed] = useState(false);

  // Only show for demo users
  if (plan !== 'demo') return null;
  if (!showTrialWarning && !isTrialExpired) return null;
  if (dismissed && !isTrialExpired) return null; // Can't dismiss expired banner

  if (isTrialExpired) {
    return (
      <div className="w-full bg-red-900/40 border-b border-red-500/40 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Lock className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-sm text-red-200">
              Your 30-day demo access has ended. You are now on limited demo access.
              To unlock Core or Pro, reach out to{' '}
              <a
                href="mailto:founder@nebulafinance.com"
                className="underline text-red-300 hover:text-white font-medium"
              >
                founder@nebulafinance.com
              </a>
            </p>
          </div>
          <a
            href="mailto:founder@nebulafinance.com?subject=Nebula Finance - Upgrade to Core/Pro&body=Hi Aravind, I'd like to explore upgrading my Nebula Finance access."
            className="shrink-0 px-3 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
          >
            <Mail className="w-3 h-3" />
            Contact Us
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-yellow-900/30 border-b border-yellow-500/30 px-4 py-2.5">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0" />
          <p className="text-sm text-yellow-200">
            You are on a 1-month demo access.{' '}
            <span className="font-semibold text-yellow-300">
              {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining.
            </span>{' '}
            For Core or Pro pricing, contact{' '}
            <a
              href="mailto:founder@nebulafinance.com"
              className="underline text-yellow-300 hover:text-white font-medium"
            >
              founder@nebulafinance.com
            </a>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href="mailto:founder@nebulafinance.com?subject=Nebula Finance - Upgrade Enquiry&body=Hi Aravind, I'd like to learn more about Core and Pro pricing."
            className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
          >
            <Mail className="w-3 h-3" />
            Get Pricing
          </a>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 text-yellow-400 hover:text-yellow-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
